import {
  DateRangeFilterConfig,
  FilterConfig,
  FilterGroupConfig,
  InputFilterConfig,
  SearchInputFilterConfig,
} from 'components/SearchAndFilters/FilterTypes';
import {
  MultiSelectFilterConfig,
  MultiSelectRenderStyle,
} from 'components/SearchAndFilters/MultiSelect';
import { useAtomValue, useSetAtom } from 'jotai';
import React, { useEffect, useRef, useState } from 'react';
import { fetchIntegrationOptions } from 'views/Admin/Dashboard/BPADashboard/IntegrationListing/services/integrationListingService';
import { companyIdAtom } from 'views/state';

import {
  isBpaQuerystringFiltersInitializedAtom,
  parsedFilterValuesAtom,
} from './state';

export const useFilterConfig = (): {
  config: FilterConfig;
  initKey: number;
} => {
  const parsedFilterValues = useAtomValue(parsedFilterValuesAtom);
  const companyId = useAtomValue(companyIdAtom);

  const [providerOptions, setProviderOptions] = useState<
    { title: string; value: string }[]
  >([]);
  const [benchmarkOptions, setBenchmarkOptions] = useState<
    { title: string; value: string }[]
  >([]);
  const [moduleOptions, setModuleOptions] = useState<
    { title: string; value: string }[]
  >([]);

  const setQuerystringFiltersInitialized = useSetAtom(
    isBpaQuerystringFiltersInitializedAtom,
  );

  useEffect(() => {
    setQuerystringFiltersInitialized(false);
    // Querystring filter parsing is not yet implemented.
    // Immediately resolve so strict-mode double-invoke does not stall the grid fetch.
    setQuerystringFiltersInitialized(true);

    return () => {
      setQuerystringFiltersInitialized(false);
    };
  }, [setQuerystringFiltersInitialized]);

  useEffect(() => {
    if (!companyId || companyId === -1) return;
    fetchIntegrationOptions(companyId, 'providers,benchmarks,modules')
      .then((res) => {
        if (res.status === 200 && res.data) {
          const providers = res.data.providers || [];
          const benchmarks = res.data.benchmarks || [];
          const modules = res.data.modules || [];

          setProviderOptions(providers);
          setBenchmarkOptions(benchmarks);
          setModuleOptions(modules);
        }
      })
      .catch((err) => {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn('Failed to load BPA integration filter options:', err);
        }
      });
  }, [companyId]);

  // Build a new FilterConfig only when the async option arrays change.
  // Do NOT include filterValues in this memo — it would rebuild the entire
  // config (and all its class instances) on every keypress, causing flicker.
  const config = React.useMemo(() => {
    const newConfig = new FilterConfig({
      searchFilterId: 'search',
      leftPanelFilters: ['dateRangeFilters', 'account_id', 'benchmark'],
      rightPanelFilters: ['status', 'trigger_type', 'provider', 'module'],
      filters: [
        new SearchInputFilterConfig({
          id: 'search',
          label: 'Search',
          placeholder: 'Search scans…',
          maxLength: 250,
          debounceTime: 600,
          enableDebounce: true,
          inputProps: { type: 'text' },
          showInFilterMenu: false,
        }),
        new FilterGroupConfig({
          id: 'dateRangeFilters',
          label: 'Filter by Date',
          iconClass: 'fas fa-calendar-alt',
          tooltip:
            'Filter by scan dates and timestamps. The From and To dates are inclusive.',
          filters: [
            new DateRangeFilterConfig({
              id: 't12_created_at',
              label: 'Created At',
              labelWidth: '100px',
            }),
            new DateRangeFilterConfig({
              id: 't12_updated_at',
              label: 'Updated At',
              labelWidth: '100px',
            }),
            new DateRangeFilterConfig({
              id: 'started_at',
              label: 'Started At',
              labelWidth: '100px',
            }),
            new DateRangeFilterConfig({
              id: 'finished_at',
              label: 'Finished At',
              labelWidth: '100px',
            }),
          ],
        }),
        new InputFilterConfig({
          id: 'account_id',
          label: 'Account ID',
          tooltip: 'Filter by Account ID',
          iconClass: 'fas fa-id-badge',
          inputProps: {
            type: 'number',
            placeholder: 'Account ID',
            min: 1,
            step: 1,
          },
          debounceTime: 600,
          enableDebounce: true,
        }),
        new MultiSelectFilterConfig({
          id: 'status',
          label: 'Status',
          tooltip: 'Filter by scan status',
          iconClass: 'fas fa-info-circle',
          defaultSelectedValues: [''],
          options: [
            { label: 'All', value: '', deselectOthers: true },
            { label: 'Completed', value: 'COMPLETED' },
            {
              label: 'In Progress',
              value: 'IN_PROGRESS',
            },
            { label: 'Queued', value: 'QUEUED' },
            { label: 'Retry Pending', value: 'RETRY_PENDING' },
            { label: 'Failed', value: 'FAILED' },
          ],
          renderStyle: MultiSelectRenderStyle.Badges,
        }),
        new MultiSelectFilterConfig({
          id: 'trigger_type',
          label: 'Scan Type',
          tooltip: 'Filter by scan type',
          iconClass: 'fas fa-bolt',
          defaultSelectedValues: [''],
          options: [
            { label: 'All', value: '', deselectOthers: true },
            { label: 'Manual', value: 'manual', deselectOthers: true },
            { label: 'Scheduled', value: 'scheduled', deselectOthers: true },
          ],
          renderStyle: MultiSelectRenderStyle.Badges,
        }),
        new MultiSelectFilterConfig({
          id: 'provider',
          label: 'Provider',
          placeholder: 'Select provider',
          tooltip: 'Filter by cloud provider',
          iconClass: 'fas fa-cloud',
          defaultSelectedValues: [''],
          enabled: providerOptions.length > 1,
          options: [
            { label: 'All', value: '', deselectOthers: true },
            ...providerOptions.map((o) => ({
              label: o.value,
              value: o.value,
            })),
          ],
          renderStyle:
            providerOptions.length > 10
              ? MultiSelectRenderStyle.Dropdown
              : MultiSelectRenderStyle.Badges,
        }),
        new MultiSelectFilterConfig({
          id: 'module',
          label: 'Module',
          placeholder: 'Select module',
          tooltip: 'Filter by security module',
          iconClass: 'fas fa-cube',
          defaultSelectedValues: [''],
          enabled: moduleOptions.length > 1,
          options: [
            { label: 'All', value: '', deselectOthers: true },
            ...moduleOptions.map((o) => ({
              label: o.value,
              value: o.value,
            })),
          ],
          renderStyle:
            moduleOptions.length > 10
              ? MultiSelectRenderStyle.Dropdown
              : MultiSelectRenderStyle.Badges,
        }),
        new MultiSelectFilterConfig({
          id: 'benchmark',
          label: 'Benchmark',
          placeholder: 'Select benchmark',
          tooltip: 'Filter by benchmark',
          iconClass: 'fas fa-check-double',
          defaultSelectedValues: [''],
          enabled: benchmarkOptions.length > 1,
          options: [
            { label: 'All', value: '', deselectOthers: true },
            ...benchmarkOptions.map((o) => ({
              label: o.title,
              value: o.value,
            })),
          ],
          renderStyle:
            benchmarkOptions.length > 10
              ? MultiSelectRenderStyle.Dropdown
              : MultiSelectRenderStyle.Badges,
        }),
      ],
    });

    return newConfig;
  }, [providerOptions, benchmarkOptions, moduleOptions]);

  // Apply persisted filter values exactly once per config instance.
  // Using a ref flag prevents re-applying when filterValues changes after
  // the user has already interacted with the filters.
  // initKey is incremented after applying so consumers can key on it and
  // remount when the config is ready (fixes stale badges on page return).
  const initAppliedRef = useRef(false);
  const [initKey, setInitKey] = useState(0);
  useEffect(() => {
    initAppliedRef.current = false;
  }, [config]);
  useEffect(() => {
    if (!initAppliedRef.current) {
      config.applyInitialFilterValues(parsedFilterValues);
      initAppliedRef.current = true;
      setInitKey((k) => k + 1);
    }
  }, [config, parsedFilterValues]); // eslint-disable-line

  return { config, initKey };
};
